import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Trash2, Archive, ArchiveRestore, Eye, EyeOff, Loader2 } from "lucide-react";

// Hook di selezione multipla per i registri Admin.
export function useBulkSelect() {
  const [selected, setSelected] = useState([]);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = (ids) => setSelected((s) => (s.length >= ids.length && ids.every((i) => s.includes(i)) ? [] : ids));
  const clear = () => setSelected([]);
  return { selected, toggle, toggleAll, clear, isSelected: (id) => selected.includes(id), count: selected.length };
}

const VERB = { delete: "eliminati", archive: "archiviati", restore: "ripristinati" };

async function runBulk(endpoint, ids, mode) {
  const { data } = await api.post(endpoint, { ids, mode });
  toast.success(`${data.affected} ${mode === "delete" ? "elemento/i eliminato/i" : mode === "archive" ? "archiviato/i" : "ripristinato/i"}`);
  return data;
}

// Barra azioni massive: visibile sopra ogni registro.
export function BulkBar({ endpoint, selected, onDone, showArchived, onToggleArchived, entityLabel = "elementi" }) {
  const [busy, setBusy] = useState("");
  const doAction = async (mode) => {
    setBusy(mode);
    try { await runBulk(endpoint, selected, mode); onDone(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };
  const n = selected.length;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border/60 bg-sage/30 px-4 py-2.5" data-testid="bulk-bar">
      <div className="text-sm font-medium text-forest" data-testid="bulk-count">
        {n > 0 ? `${n} ${entityLabel} selezionati` : `Seleziona ${entityLabel} per eliminare o archiviare`}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={onToggleArchived} data-testid="toggle-archived" className="h-8 gap-1.5">
          {showArchived ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          {showArchived ? "Nascondi archiviati" : "Mostra archiviati"}
        </Button>
        {n > 0 && (
          <>
            {showArchived ? (
              <Button size="sm" variant="outline" onClick={() => doAction("restore")} disabled={!!busy} data-testid="bulk-restore" className="h-8 gap-1.5 border-forest/40 text-forest">
                {busy === "restore" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArchiveRestore className="h-3.5 w-3.5" />} Ripristina
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={() => doAction("archive")} disabled={!!busy} data-testid="bulk-archive" className="h-8 gap-1.5 border-amber-400 text-amber-700">
                {busy === "archive" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Archive className="h-3.5 w-3.5" />} Archivia
              </Button>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="destructive" disabled={!!busy} data-testid="bulk-delete" className="h-8 gap-1.5">
                  {busy === "delete" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Elimina ({n})
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="bulk-delete-confirm">
                <AlertDialogHeader>
                  <AlertDialogTitle>Eliminare definitivamente {n} {entityLabel}?</AlertDialogTitle>
                  <AlertDialogDescription>L'operazione è irreversibile e rimuove i dati dal database.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="bulk-delete-cancel">Annulla</AlertDialogCancel>
                  <AlertDialogAction onClick={() => doAction("delete")} data-testid="bulk-delete-confirm-btn" className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </div>
    </div>
  );
}

// Checkbox di riga.
export function RowCheck({ id, selected, onToggle }) {
  return (
    <Checkbox checked={selected} onCheckedChange={() => onToggle(id)} data-testid={`row-check-${id}`} aria-label="Seleziona riga" />
  );
}

// Azioni per singola riga: elimina (con conferma) + archivia/ripristina.
export function RowActions({ endpoint, id, archived, onDone }) {
  const [busy, setBusy] = useState(false);
  const doAction = async (mode) => {
    setBusy(true);
    try { await runBulk(endpoint, [id], mode); onDone(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center justify-end gap-1">
      {archived ? (
        <Button size="icon" variant="ghost" onClick={() => doAction("restore")} disabled={busy} title="Ripristina" data-testid={`row-restore-${id}`} className="h-7 w-7 text-forest">
          <ArchiveRestore className="h-3.5 w-3.5" />
        </Button>
      ) : (
        <Button size="icon" variant="ghost" onClick={() => doAction("archive")} disabled={busy} title="Archivia" data-testid={`row-archive-${id}`} className="h-7 w-7 text-amber-700">
          <Archive className="h-3.5 w-3.5" />
        </Button>
      )}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button size="icon" variant="ghost" disabled={busy} title="Elimina" data-testid={`row-delete-${id}`} className="h-7 w-7 text-red-600">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare definitivamente questo elemento?</AlertDialogTitle>
            <AlertDialogDescription>L'operazione è irreversibile e rimuove i dati dal database.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={() => doAction("delete")} data-testid={`row-delete-confirm-${id}`} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
